import { Alert, Button, Card, Col, Divider, Row } from "antd";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { IDeliveryWorker } from "../../../models/delivery-workers/response";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import TripContext from "../../../context/trip/context";
import TripContextProvider from "../../../context/trip/provider";
import {
  ITripRequest,
  ITripStructureRequest,
  TripCreationErrorKey,
} from "../../../models/trip/request";
import { ITrip, TripStatus } from "../../../models/trip/response";
import { IOrderDetails } from "../../../models/order/response";
import { OrderStatus } from "../../../models/order/enum";
import { IStore } from "../../../models/store/response";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import TripRoutePlanner, {
  ITripRoutePlannerOrder,
} from "../trip-route-planner";
import styles from "./style.module.scss";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import { createActiveNamlieahStoreFilters } from "../../../utils/helpers/store-filters";
import TripStartCheckModal, {
  fetchTripStartCheck,
  isAmbiguousTripRequestError,
} from "../trip-start-check-modal";
import {
  getTripStartCheckErrorKey,
  getTripStartCheckErrorText,
  isTripStartCheckRefreshError,
  ITripStartCheck,
  parseTripStartCheck,
  TripStartCheckErrorKey,
} from "../../../models/trip/start-check";
import {
  btnNotification,
  errorNotification,
} from "../../../utils/helpers/notification";
import getApiErrorMessage from "../../../utils/api/get-api-error-message";
import AuthContext from "../../../context/auth/context";
import { check } from "../../../utils/rbac/can";
import { Permissions } from "../../../utils/Permissions/Permissions";
import {
  buildTripStructureRequest,
  clearTripCreateAttempt,
  getOrCreateTripCreateAttempt,
  getPendingTripCreateAttempt,
  TripCreateIntent,
} from "./create-attempt";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { details, loading, actions } = useContext(TripContext);
  const { grantedPermissions, userDetails } = useContext(AuthContext);
  const { id } = useParams();
  const canViewTripStartCheck = check(
    grantedPermissions ?? [],
    Permissions.CAN_VIEW_TRIP_START_CHECK,
  );

  const createAttemptOwnerKey = userDetails?.employee?.user?.id
    ? `user:${userDetails.employee.user.id}`
    : userDetails?.email
      ? `email:${userDetails.email}`
      : null;
  const recoveredOwnerRef = useRef<string | null>(null);
  const creationRequestLockRef = useRef(false);

  useEffect(() => {
    if (id) {
      actions.getDetails(Number(id));
    } else {
      actions.reset();
    }
  }, [id]);

  useEffect(() => {
    if (id && details && details.status !== TripStatus.PENDING) {
      navigate("/trips/details/" + details.id, { replace: true });
    }
  }, [details?.id, details?.status, id, navigate]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [driversWithStoreData, setDriversWithStoreData] = useState<
    IDeliveryWorker[]
  >([]);
  const [stores, setStores] = useState<any[]>([]);
  const [storesWithLocation, setStoresWithLocation] = useState<IStore[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | undefined>(
    undefined,
  );
  const [selectedClassificationId, setSelectedClassificationId] = useState<
    number | undefined
  >(undefined);
  const [selectedStatus, setSelectedStatus] = useState<TripStatus | undefined>(
    undefined,
  );
  const [routePlannerOrders, setRoutePlannerOrders] = useState<
    ITripRoutePlannerOrder[]
  >([]);
  const [recoveredOrderIds, setRecoveredOrderIds] = useState<number[]>([]);
  const [unassignedOrdersWithLocation, setUnassignedOrdersWithLocation] =
    useState<IOrderDetails[]>([]);

  const methods = useForm<ITripRequest>({
    defaultValues: {
      driverId: undefined,
      orderIds: [],
    },
  });

  const { handleSubmit, reset } = methods;
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingCreateAndStart, setLoadingCreateAndStart] = useState(false);
  const [loadingStartTrip, setLoadingStartTrip] = useState(false);
  const [loadingCancelTrip, setLoadingCancelTrip] = useState(false);
  // Start-check confirmation modal (products stored outside the basket)
  const [startCheck, setStartCheck] = useState<ITripStartCheck | null>(null);
  const [startCheckOpen, setStartCheckOpen] = useState(false);
  const [startConfirming, setStartConfirming] = useState(false);
  const [startCheckReviewMessage, setStartCheckReviewMessage] = useState<
    string | null
  >(null);
  const [pendingStart, setPendingStart] = useState<{
    tripId: number;
    fromCreate: boolean;
  } | null>(null);
  const [createdTripId, setCreatedTripId] = useState<number | null>(null);
  const [creationOutcomeUnknown, setCreationOutcomeUnknown] = useState(false);
  const [unknownCreationIntent, setUnknownCreationIntent] =
    useState<TripCreateIntent | null>(null);
  const [refreshingData, setRefreshingData] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingClassifications, setLoadingClassifications] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [unassignedOrders, setUnassignedOrders] = useState<any[]>([]);

  const fetchStores = async () => {
    setLoadingStores(true);
    try {
      const response = await EndPoints.store.getAllStores({
        light: false,
        perPage: -1,
        filters: createActiveNamlieahStoreFilters(),
      });
      setStoresWithLocation(response.data.data);
      setStores(
        response.data.data.map((store: any) => ({
          label: store.name,
          value: store.id,
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingStores(false);
    }
  };

  const fetchClassifications = async () => {
    setLoadingClassifications(true);
    try {
      const response =
        await EndPoints.driverClassification.getAllDriverClassifications({
          light: true,
          perPage: -1,
        });
      setClassifications(
        response.data.data.map((classification: any) => ({
          label: classification.name,
          value: classification.id,
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingClassifications(false);
    }
  };

  const fetchDrivers = async (storeId?: number, classificationId?: number) => {
    setLoadingDrivers(true);
    try {
      const filters: any[] = [
        {
          name: "status",
          value: 1,
          operation: "eq",
        },
        {
          name: "statusWorker",
          value: 1,
          operation: "eq",
        },
      ];

      if (storeId) {
        filters.push({
          name: "storeId",
          value: storeId,
          operation: "eq",
        });
      }

      if (classificationId) {
        filters.push({
          name: "driverClassificationId",
          value: classificationId,
          operation: "eq",
        });
      }

      const response = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
        light: false,
        perPage: -1,
        filters,
      });
      const driversData = response.data.data;
      setDriversWithStoreData(driversData);
      setDrivers(
        driversData.map((driver: IDeliveryWorker) => ({
          label:
            `${driver.user?.firstName || ""} ${driver.user?.lastName || ""}`.trim() ||
            `Driver ${driver.id}`,
          value: driver.id,
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const fetchUnassignedOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await EndPoints.trip.getUnassignedOrders({
        light: false,
        perPage: -1,
      });
      setUnassignedOrdersWithLocation(response.data.data);
      setUnassignedOrders(
        response.data.data.map((order: any) => ({
          label: `${order.number} - ${order.client?.firstName} ${order.client?.lastName}`,
          value: order.id,
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const refreshDropdownData = async () => {
    setRefreshingData(true);
    try {
      await Promise.all([
        fetchStores(),
        fetchClassifications(),
        fetchDrivers(selectedStoreId, selectedClassificationId),
        fetchUnassignedOrders(),
      ]);
    } finally {
      setRefreshingData(false);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchClassifications();
    fetchUnassignedOrders();
    // Note: fetchDrivers is called by the useEffect below that watches store/classification filters
  }, []);

  useEffect(() => {
    // This runs on mount (with undefined values) and whenever filters change
    fetchDrivers(selectedStoreId, selectedClassificationId);
  }, [selectedStoreId, selectedClassificationId]);

  useEffect(() => {
    if (id && details) {
      reset({
        driverId: details.driverId,
        orderIds: details.orders?.map((order) => order.id) || [],
        status: details.status,
        reason: details.cancellationReason,
      });
      setSelectedStatus(details.status);

      // Ensure the current driver is in the dropdown options when editing
      if (details.driver && details.driverId) {
        // Driver name can be directly on driver object or nested under user
        let driverName = "";
        if (details.driver.firstName || details.driver.lastName) {
          driverName =
            `${details.driver.firstName || ""} ${details.driver.lastName || ""}`.trim();
        } else if (details.driver.user) {
          driverName =
            `${details.driver.user.firstName || ""} ${details.driver.user.lastName || ""}`.trim();
        }
        if (!driverName) {
          driverName = `Driver ${details.driverId}`;
        }

        setDrivers((prevDrivers) => {
          // Check if driver is already in the list
          const exists = prevDrivers.some((d) => d.value === details.driverId);
          if (!exists) {
            return [
              { label: driverName, value: details.driverId },
              ...prevDrivers,
            ];
          }
          return prevDrivers;
        });
      }
    }
  }, [details, id]);

  // Initialize route planner orders from details
  // Handle both client object (from unassigned orders) and clientName string (from trip orders)
  useEffect(() => {
    if (details?.orders) {
      const plannerOrders: ITripRoutePlannerOrder[] = details.orders.map(
        (order: any) => {
          // Build client object from clientName if client object is not available
          let client = order.client;
          if (!client && order.clientName) {
            // Split clientName into firstName and lastName
            const nameParts = order.clientName.trim().split(" ");
            client = {
              firstName: nameParts[0] || "",
              lastName: nameParts.slice(1).join(" ") || "",
            };
          }

          return {
            id: order.id,
            number: order.number,
            status: order.status,
            sequence: order.pivot?.sequence || 0,
            distanceToNext: order.pivot?.distanceToNext || 0,
            client,
            clientAddress: order.clientAddress,
            location: order.location,
          };
        },
      );
      setRoutePlannerOrders(plannerOrders);
    }
  }, [details]);

  useEffect(() => {
    if (id || !creationOutcomeUnknown || recoveredOrderIds.length === 0) return;

    const knownOrders = new Map(
      unassignedOrdersWithLocation.map((order) => [order.id, order]),
    );

    setRoutePlannerOrders((previous) => {
      const previousOrders = new Map(
        previous.map((order) => [order.id, order]),
      );

      return recoveredOrderIds.map((orderId, index) => {
        const knownOrder = knownOrders.get(orderId);
        if (!knownOrder) {
          return (
            previousOrders.get(orderId) ?? {
              id: orderId,
              number: String(orderId),
              status:
                OrderStatus.SEARCHING_FOR_DELIVERY_DRIVER as unknown as string,
              sequence: index + 1,
            }
          );
        }

        return {
          id: knownOrder.id,
          number: knownOrder.number,
          status: knownOrder.status as unknown as string,
          sequence: index + 1,
          client: knownOrder.client,
          clientAddress: knownOrder.clientAddress,
          location: knownOrder.location,
        };
      });
    });
  }, [
    creationOutcomeUnknown,
    id,
    recoveredOrderIds,
    unassignedOrdersWithLocation,
  ]);

  // Watch the driverId field to get selected driver's store
  const selectedDriverId = useWatch({
    control: methods.control,
    name: "driverId",
  });

  // Get selected driver's store location (prioritized over filter store)
  const selectedDriverStore = useMemo(() => {
    if (selectedDriverId) {
      const driver = driversWithStoreData.find(
        (d) => d.id === selectedDriverId,
      );
      return driver?.store;
    }
    return undefined;
  }, [selectedDriverId, driversWithStoreData]);

  // Get selected store location - driver's store takes priority
  // When editing, use store from details.driver.store or details.store directly (has location data)
  const detailsStore = details?.driver?.store || details?.store;
  const selectedStore =
    selectedDriverStore ||
    storesWithLocation.find((s) => s.id === selectedStoreId) ||
    detailsStore ||
    (detailsStore?.id
      ? storesWithLocation.find((s) => s.id === detailsStore.id)
      : undefined);

  // Handle route planner order changes
  const handleRoutePlannerOrdersChange = useCallback(
    (orders: ITripRoutePlannerOrder[]) => {
      setRoutePlannerOrders(orders);
      methods.setValue(
        "orderIds",
        orders.map((o) => o.id),
      );
    },
    [methods],
  );

  // Handle adding order from route planner
  const handleRoutePlannerAddOrder = useCallback((order: IOrderDetails) => {
    // Order will be added via handleRoutePlannerOrdersChange
  }, []);

  // Handle removing order from route planner
  const handleRoutePlannerRemoveOrder = useCallback((orderId: number) => {
    // Order will be removed via handleRoutePlannerOrdersChange
  }, []);

  // Filter unassigned orders for route planner (exclude already assigned ones)
  const filteredUnassignedOrders = unassignedOrdersWithLocation.filter(
    (order) => !routePlannerOrders.some((ro) => ro.id === order.id),
  );
  const isStructureEditable =
    !id ||
    (Number(details?.id) === Number(id) &&
      details?.status === TripStatus.PENDING);

  const statusOptions = [
    { label: t("preparing"), value: TripStatus.PENDING },
    ...(details?.status === TripStatus.ACTIVE
      ? [{ label: t("delivery_in_progress"), value: TripStatus.ACTIVE }]
      : []),
    { label: t("completed"), value: TripStatus.COMPLETED },
    { label: t("canceled"), value: TripStatus.CANCELLED },
  ];

  const createTripSafely = async (
    data: ITripRequest,
    includeStartCheck: boolean,
    intent: TripCreateIntent,
  ): Promise<ITrip> => {
    if (!createAttemptOwnerKey) {
      errorNotification(t("operation_failed"));
      throw new Error("Trip creation owner is unavailable");
    }

    const attempt = getOrCreateTripCreateAttempt(
      data,
      createAttemptOwnerKey,
      intent,
    );
    const requestCreate = (suppressErrorNotification: boolean) => {
      const params = suppressErrorNotification
        ? { suppressErrorNotification: true }
        : undefined;

      return includeStartCheck
        ? EndPoints.trip.createTripWithStartCheck(attempt.request, params)
        : EndPoints.trip.createTrip(attempt.request, params);
    };

    try {
      let response;
      try {
        response = await requestCreate(true);
      } catch (firstError) {
        if (!isAmbiguousTripRequestError(firstError)) {
          clearTripCreateAttempt(attempt);
          errorNotification(
            getApiErrorMessage(firstError, t("operation_failed")),
          );
          throw firstError;
        }

        // One bounded retry only. It reuses the exact same token and payload.
        response = await requestCreate(false);
      }

      clearTripCreateAttempt(attempt);
      setCreationOutcomeUnknown(false);
      setUnknownCreationIntent(null);
      setRecoveredOrderIds([]);
      return response.data;
    } catch (error: any) {
      const isAmbiguous = isAmbiguousTripRequestError(error);
      setCreationOutcomeUnknown(isAmbiguous);
      setUnknownCreationIntent(isAmbiguous ? intent : null);
      // Keep an ambiguous attempt in sessionStorage so a later user retry
      // safely reuses its identity instead of creating a second trip.
      if (!isAmbiguous) {
        clearTripCreateAttempt(attempt);
        setRecoveredOrderIds([]);
      }

      // A conflicting token cannot safely identify this creation anymore.
      // Returning to the list forces a fresh server view; a later explicit
      // create action is then a new human-approved attempt with a new token.
      if (
        error?.response?.data?.errorKey === TripCreationErrorKey.TOKEN_CONFLICT
      ) {
        navigate("/trips", { replace: true });
      }
      throw error;
    }
  };

  const onSubmit = async (
    data: ITripRequest,
    statusOverride?: TripStatus,
    setLoadingOverride?: (loading: boolean) => void,
  ) => {
    if (!id && createdTripId) {
      navigate(`/trips/details/${createdTripId}`, { replace: true });
      return;
    }

    const isCreateAction = !id;
    if (isCreateAction) {
      if (creationRequestLockRef.current) return;
      creationRequestLockRef.current = true;
    }

    const setLoading = setLoadingOverride || setLoadingSubmit;
    const submitData: ITripRequest = {
      ...data,
      ...(statusOverride ? { status: statusOverride } : {}),
    };

    // A trip can only transition to ACTIVE through the guarded Start action.
    if (
      details?.status !== TripStatus.ACTIVE &&
      submitData.status === TripStatus.ACTIVE
    ) {
      submitData.status = details?.status ?? TripStatus.PENDING;
    }

    if (submitData.status !== TripStatus.CANCELLED) {
      delete submitData.reason;
    } else if (!submitData.reason) {
      submitData.reason = "Cancelled from admin panel";
    }

    await execute({
      callback: async () => {
        setLoading(true);
        if (id) {
          if (!details || Number(details.id) !== Number(id)) return;
          await EndPoints.trip.updateTrip(details.id, submitData);
        } else {
          await createTripSafely(submitData, false, "create");
        }
        navigate(-1);
      },
      fallback: () => {},
      finallyCallback: () => {
        setLoading(false);
        if (isCreateAction) creationRequestLockRef.current = false;
      },
      throwException: false,
    });
  };

  const closeStartCheck = () => {
    setStartCheckOpen(false);
    setStartCheck(null);
    setStartCheckReviewMessage(null);
    setPendingStart(null);
  };

  const finishCreatedTripAsPending = (tripId: number) => {
    closeStartCheck();
    btnNotification(t("trip_start_check_created_pending"));
    navigate(`/trips/details/${tripId}`, { replace: true });
  };

  const openRequiredStartCheck = (
    tripId: number,
    checkSnapshot: ITripStartCheck,
    fromCreate: boolean,
    reviewMessage?: string,
  ): boolean => {
    if (!checkSnapshot.required) return false;

    setPendingStart({ tripId, fromCreate });
    setStartCheck(checkSnapshot);
    setStartCheckReviewMessage(reviewMessage ?? null);
    setStartCheckOpen(true);
    return true;
  };

  const completeStartedTrip = (tripId: number) => {
    closeStartCheck();
    navigate("/trips/details/" + tripId, { replace: true });
  };

  const performStart = async (
    tripId: number,
    checkSnapshot: ITripStartCheck | null,
    fromCreate: boolean,
    allowAutomaticRetry = true,
  ): Promise<boolean> => {
    try {
      await EndPoints.trip.startTrip(
        tripId,
        checkSnapshot?.required
          ? {
              status: TripStatus.ACTIVE,
              startCheckConfirmed: true,
              startCheckToken: checkSnapshot.startCheckToken,
            }
          : { status: TripStatus.ACTIVE },
        { suppressErrorNotification: true },
      );
      completeStartedTrip(tripId);
      return true;
    } catch (error) {
      const reviewMessage = getTripStartCheckErrorText(error);

      if (isTripStartCheckRefreshError(error)) {
        try {
          const fresh = await fetchTripStartCheck(tripId);
          if (fresh.required) {
            return openRequiredStartCheck(
              tripId,
              fresh,
              fromCreate,
              reviewMessage,
            );
          }

          if (allowAutomaticRetry) {
            return performStart(tripId, null, fromCreate, false);
          }
          errorNotification(getApiErrorMessage(error, t("operation_failed")));
          return false;
        } catch {
          return false;
        }
      }

      if (isAmbiguousTripRequestError(error)) {
        try {
          const { data: reconciledTrip } = await EndPoints.trip.getTrip(tripId);
          if (reconciledTrip.status === TripStatus.ACTIVE) {
            completeStartedTrip(tripId);
            return true;
          }

          if (reconciledTrip.status !== TripStatus.PENDING) {
            closeStartCheck();
            navigate("/trips/details/" + tripId, { replace: true });
            return true;
          }

          const fresh = await fetchTripStartCheck(tripId);
          if (fresh.required) {
            return openRequiredStartCheck(
              tripId,
              fresh,
              fromCreate,
              reviewMessage,
            );
          }

          if (allowAutomaticRetry) {
            return performStart(tripId, null, fromCreate, false);
          }
          errorNotification(getApiErrorMessage(error, t("operation_failed")));
          return false;
        } catch {
          if (fromCreate) {
            closeStartCheck();
            navigate("/trips/details/" + tripId, { replace: true });
            return true;
          }
          return false;
        }
      }

      if (
        getTripStartCheckErrorKey(error) ===
        TripStartCheckErrorKey.ACTIVE_STRUCTURE_LOCKED
      ) {
        try {
          const { data: reconciledTrip } = await EndPoints.trip.getTrip(tripId);
          if (reconciledTrip.status !== TripStatus.PENDING) {
            closeStartCheck();
            navigate("/trips/details/" + tripId, { replace: true });
            return true;
          }
        } catch {
          // The interceptor reports the refresh error; remain fail-closed.
          return false;
        }
      }

      errorNotification(getApiErrorMessage(error, t("operation_failed")));
      return false;
    }
  };

  // Exact sequence: save structure, fetch a fresh snapshot, then start-only.
  const startExistingTrip = async (data: ITripRequest) => {
    if (!details || !canViewTripStartCheck) return;
    setLoadingStartTrip(true);
    try {
      const structure: ITripStructureRequest = buildTripStructureRequest(data);
      await EndPoints.trip.updateTrip(details.id, structure);

      const check = await fetchTripStartCheck(details.id);
      if (check.required) {
        openRequiredStartCheck(details.id, check, false);
        return;
      }

      await performStart(details.id, null, false);
    } catch (error) {
      if (
        getTripStartCheckErrorKey(error) ===
        TripStartCheckErrorKey.ACTIVE_STRUCTURE_LOCKED
      ) {
        try {
          const { data: reconciledTrip } = await EndPoints.trip.getTrip(
            details.id,
          );
          if (reconciledTrip.status !== TripStatus.PENDING) {
            navigate("/trips/details/" + details.id, { replace: true });
          }
        } catch {
          // The interceptor reports the refresh error; remain fail-closed.
        }
      }
    } finally {
      setLoadingStartTrip(false);
    }
  };

  // POST/replay returns the trip's current state and optional startCheck.
  const createAndStartTrip = async (data: ITripRequest) => {
    if (!canViewTripStartCheck) return;
    if (createdTripId) {
      navigate("/trips/details/" + createdTripId, { replace: true });
      return;
    }
    if (creationRequestLockRef.current) return;
    creationRequestLockRef.current = true;

    setLoadingCreateAndStart(true);
    let createdIdForAttempt: number | null = null;
    try {
      const created = await createTripSafely(data, true, "create-and-start");
      const tripId = Number(created.id);
      if (!Number.isFinite(tripId) || tripId <= 0) {
        errorNotification(t("operation_failed"));
        navigate("/trips", { replace: true });
        return;
      }

      createdIdForAttempt = tripId;
      setCreatedTripId(tripId);

      if (created.status !== TripStatus.PENDING) {
        closeStartCheck();
        navigate("/trips/details/" + tripId, { replace: true });
        return;
      }
      const check = parseTripStartCheck(created.startCheck);
      if (check.required) {
        openRequiredStartCheck(tripId, check, true);
        return;
      }

      const handled = await performStart(tripId, null, true);
      if (!handled) finishCreatedTripAsPending(tripId);
    } catch {
      // A known id means POST committed; never expose create mode again.
      if (createdIdForAttempt) {
        finishCreatedTripAsPending(createdIdForAttempt);
      }
    } finally {
      setLoadingCreateAndStart(false);
      creationRequestLockRef.current = false;
    }
  };

  const handleStartCheckConfirm = async () => {
    if (!pendingStart || !startCheck?.required || startConfirming) return;
    setStartConfirming(true);
    try {
      const handled = await performStart(
        pendingStart.tripId,
        startCheck,
        pendingStart.fromCreate,
      );

      if (!handled && pendingStart.fromCreate) {
        finishCreatedTripAsPending(pendingStart.tripId);
      }
    } finally {
      setStartConfirming(false);
    }
  };

  const handleStartCheckCancel = () => {
    if (startConfirming) return;

    if (pendingStart?.fromCreate) {
      finishCreatedTripAsPending(pendingStart.tripId);
      return;
    }

    closeStartCheck();
  };
  useEffect(() => {
    if (
      id ||
      !createAttemptOwnerKey ||
      recoveredOwnerRef.current === createAttemptOwnerKey
    ) {
      return;
    }

    const pendingAttempt = getPendingTripCreateAttempt(createAttemptOwnerKey);
    if (!pendingAttempt) return;
    if (creationRequestLockRef.current) return;
    creationRequestLockRef.current = true;

    setRecoveredOrderIds(pendingAttempt.request.orderIds);
    recoveredOwnerRef.current = createAttemptOwnerKey;
    reset({
      driverId: pendingAttempt.request.driverId,
      orderIds: pendingAttempt.request.orderIds,
      status: TripStatus.PENDING,
    });
    setCreationOutcomeUnknown(true);

    const shouldContinueStart =
      pendingAttempt.intent === "create-and-start" && canViewTripStartCheck;
    const recoveryIntent: TripCreateIntent = shouldContinueStart
      ? "create-and-start"
      : "create";
    setUnknownCreationIntent(recoveryIntent);
    if (shouldContinueStart) {
      setLoadingCreateAndStart(true);
    } else {
      setLoadingSubmit(true);
    }

    let recoveredTripId: number | null = null;
    const recover = async () => {
      try {
        const recovered = await createTripSafely(
          pendingAttempt.request,
          shouldContinueStart,
          recoveryIntent,
        );
        const tripId = Number(recovered.id);
        if (!Number.isFinite(tripId) || tripId <= 0) {
          errorNotification(t("operation_failed"));
          navigate("/trips", { replace: true });
          return;
        }

        recoveredTripId = tripId;
        setCreatedTripId(tripId);

        if (!shouldContinueStart || recovered.status !== TripStatus.PENDING) {
          closeStartCheck();
          navigate("/trips/details/" + tripId, { replace: true });
          return;
        }

        const check = parseTripStartCheck(recovered.startCheck);
        if (check.required) {
          openRequiredStartCheck(tripId, check, true);
          return;
        }

        const handled = await performStart(tripId, null, true);
        if (!handled) finishCreatedTripAsPending(tripId);
      } catch {
        if (recoveredTripId) {
          finishCreatedTripAsPending(recoveredTripId);
        }
      } finally {
        setLoadingCreateAndStart(false);
        setLoadingSubmit(false);
        creationRequestLockRef.current = false;
      }
    };

    void recover();
  }, [canViewTripStartCheck, createAttemptOwnerKey, id]);

  const isCreatingTrip = loadingSubmit || loadingCreateAndStart;
  const isTripMutationBusy =
    isCreatingTrip || loadingStartTrip || loadingCancelTrip || startConfirming;
  const isLoadingRequestedTrip =
    Boolean(id) && Number(details?.id) !== Number(id);

  const canEditStructure =
    isStructureEditable && !isTripMutationBusy && !creationOutcomeUnknown;
  return (
    <FormProvider {...methods}>
      <form className={styles.container}>
        <div>
          <span className={styles.title}>
            {id ? t("update_trip") : t("create_trip")}
          </span>
          <Divider className={styles.divider} />
          {id && (loading.includes("details") || isLoadingRequestedTrip) ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              {creationOutcomeUnknown && (
                <Alert
                  type="warning"
                  showIcon
                  message={t("trip_creation_outcome_unknown")}
                  style={{ marginBottom: 16 }}
                />
              )}
              {/* Form Section */}
              <Card
                bordered={false}
                style={{ flexShrink: 0 }}
                extra={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 12, padding: 10, color: "#888" }}>
                      {t("refresh_dropdown_data")}
                    </span>
                    <RefreshBtn
                      key={0}
                      onClick={() => refreshDropdownData()}
                      loading={refreshingData}
                      size="small"
                    >
                      {t("refresh")}
                    </RefreshBtn>
                  </div>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="filterStoreId"
                      label={t("store")}
                      disabled={!canEditStructure}
                      input={{
                        type: "select",
                        options: stores,
                        loading: loadingStores,
                        onChange: (value: number) => {
                          setSelectedStoreId(value);
                          methods.setValue("driverId", undefined);
                        },
                      }}
                    />
                    <small
                      style={{ color: "#888", marginTop: 4, display: "block" }}
                    >
                      {t("optional_filter_for_drivers")}
                    </small>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="filterClassificationId"
                      label={t("driver_classification")}
                      disabled={!canEditStructure}
                      input={{
                        type: "select",
                        options: classifications,
                        loading: loadingClassifications,
                        onChange: (value: number) => {
                          setSelectedClassificationId(value);
                          methods.setValue("driverId", undefined);
                        },
                      }}
                    />
                    <small
                      style={{ color: "#888", marginTop: 4, display: "block" }}
                    >
                      {t("optional_filter_for_drivers")}
                    </small>
                  </Col>

                  <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="driverId"
                      label={t("driver")}
                      disabled={!canEditStructure}
                      input={{
                        type: "select",
                        options: drivers,
                        loading: loadingDrivers,
                      }}
                      rules={{ required: true }}
                    />
                    {(selectedStoreId || selectedClassificationId) && (
                      <small
                        style={{
                          color: "#3458a4",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        {t("filtered_by_store_or_classification")}
                      </small>
                    )}
                    {selectedDriverStore && (
                      <small
                        style={{
                          color: "#52c41a",
                          marginTop: 4,
                          display: "block",
                          fontWeight: 500,
                        }}
                      >
                        📍 {t("start_point")}: {selectedDriverStore.name}
                      </small>
                    )}
                  </Col>

                  {details && (
                    <>
                      <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                        <FieldBuilder
                          name="status"
                          label={t("status")}
                          disabled={!canEditStructure}
                          input={{
                            type: "select",
                            options: statusOptions,
                            onChange: (value) => setSelectedStatus(value),
                          }}
                          rules={{ required: true }}
                        />
                      </Col>
                      {selectedStatus === TripStatus.CANCELLED && (
                        <Col xs={24} sm={24} md={16} lg={16} xl={16} xxl={16}>
                          <FieldBuilder
                            name="reason"
                            label={t("cancellation_reason")}
                            input={{
                              type: "text-area",
                              rows: 2,
                            }}
                            rules={{ required: true }}
                          />
                        </Col>
                      )}
                    </>
                  )}
                </Row>
              </Card>

              {/* Route Planner / Map Section */}
              <div
                style={{
                  marginTop: 16,
                  flex: 1,
                  minHeight: 600,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <TripRoutePlanner
                  storeLocation={selectedStore?.location}
                  assignedOrders={routePlannerOrders}
                  unassignedOrders={filteredUnassignedOrders}
                  isEditable={canEditStructure}
                  isLoading={loadingOrders}
                  mapPolylines={details?.mapPolylines}
                  routeStatus={
                    details?.status === TripStatus.ACTIVE
                      ? "active"
                      : details?.status === TripStatus.COMPLETED
                        ? "completed"
                        : "pending"
                  }
                  onOrdersChange={handleRoutePlannerOrdersChange}
                  onAddOrder={handleRoutePlannerAddOrder}
                  onRemoveOrder={handleRoutePlannerRemoveOrder}
                />
              </div>
              <Divider className={styles.divider} />
              <div className={styles.formFooter}>
                <Button
                  disabled={isTripMutationBusy}
                  onClick={() => navigate(-1)}
                >
                  {t("cancel")}
                </Button>
                {details?.status === TripStatus.PENDING &&
                  canViewTripStartCheck && (
                    <Button
                      loading={loadingStartTrip}
                      disabled={isTripMutationBusy}
                      onClick={handleSubmit((data) => startExistingTrip(data))}
                      type="default"
                      style={{ borderColor: "#52c41a", color: "#52c41a" }}
                    >
                      {t("start_trip")}
                    </Button>
                  )}
                {details?.status === TripStatus.PENDING && (
                  <Button
                    danger
                    loading={loadingCancelTrip}
                    disabled={isTripMutationBusy}
                    onClick={handleSubmit((data) =>
                      onSubmit(
                        data,
                        TripStatus.CANCELLED,
                        setLoadingCancelTrip,
                      ),
                    )}
                  >
                    {t("cancel_trip")}
                  </Button>
                )}
                {!id && canViewTripStartCheck && (
                  <Button
                    loading={loadingCreateAndStart}
                    disabled={
                      createdTripId !== null ||
                      isTripMutationBusy ||
                      (creationOutcomeUnknown &&
                        unknownCreationIntent !== "create-and-start")
                    }
                    onClick={handleSubmit((data) => createAndStartTrip(data))}
                    type="default"
                    style={{ borderColor: "#52c41a", color: "#52c41a" }}
                  >
                    {t("create_and_start")}
                  </Button>
                )}
                <Button
                  loading={loadingSubmit}
                  disabled={
                    !isStructureEditable ||
                    isTripMutationBusy ||
                    (creationOutcomeUnknown &&
                      unknownCreationIntent !== "create") ||
                    (!id && createdTripId !== null)
                  }
                  onClick={handleSubmit((data) => onSubmit(data))}
                  type="primary"
                >
                  {id ? t("save") : t("create")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>

      <TripStartCheckModal
        open={startCheckOpen}
        check={startCheck}
        confirming={startConfirming}
        reviewMessage={startCheckReviewMessage}
        onConfirm={handleStartCheckConfirm}
        onCancel={handleStartCheckCancel}
      />
    </FormProvider>
  );
};

const TripFormPage = () => {
  return (
    <TripContextProvider>
      <Form />
    </TripContextProvider>
  );
};

export default TripFormPage;
